import React, { useEffect, useState } from 'react';
import {
  defaultAdjustmentForm,
  adjustmentTypeOptions,
  reasonOptions,
} from '../warehouseInventoryTypes';
import type { AdjustmentForm as AdjustmentFormData } from '../warehouseInventoryTypes';
import { useSkusStore } from '../../../../store/skusStore';
import { getInventoryView, InventoryLotView } from '../../../../services/inventoryApi';
import styles from './AdjustmentForm.module.css';

interface AdjustmentFormProps {
  onConfirm: (form: AdjustmentFormData) => Promise<void> | void;
  onCancel: () => void;
}

export const AdjustmentForm: React.FC<AdjustmentFormProps> = ({ onConfirm, onCancel }) => {
  const [form, setForm] = useState<AdjustmentFormData>({ ...defaultAdjustmentForm });
  const [lots, setLots] = useState<InventoryLotView[]>([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  useEffect(() => {
    if (skus.length === 0) fetchSkus().catch(() => undefined);
  }, [skus.length, fetchSkus]);

  // When the SKU changes, load its lots so the user can pick one.
  useEffect(() => {
    let cancelled = false;
    if (!form.skuId) { setLots([]); return; }
    setLoadingLots(true);
    getInventoryView(Number(form.skuId))
      .then((rows) => { if (!cancelled) setLots(rows); })
      .catch(() => { if (!cancelled) setLots([]); })
      .finally(() => { if (!cancelled) setLoadingLots(false); });
    return () => { cancelled = true; };
  }, [form.skuId]);

  const handleChange = (field: keyof AdjustmentFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.skuId) { setError('Select a SKU'); return; }
    if (!form.lotId) { setError('Select a lot to adjust'); return; }
    if (!form.quantity || Number(form.quantity) <= 0) { setError('Enter a quantity greater than 0'); return; }
    if (!form.reason) { setError('Select a reason'); return; }
    setSubmitting(true);
    try {
      await onConfirm(form);
      setForm({ ...defaultAdjustmentForm });
      setLots([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>Inventory Adjustment</h2>
      <form onSubmit={handleSubmit}>
        <div className={styles.grid}>
          <div className={styles.field}>
            <label className={styles.label}>SKU</label>
            <select
              className={styles.input}
              value={form.skuId}
              onChange={(e) => setForm((p) => ({ ...p, skuId: e.target.value, lotId: '' }))}
            >
              <option value="">Select SKU</option>
              {skus.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.sku_code} — {s.product_name}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Lot</label>
            <select
              className={styles.input}
              value={form.lotId}
              onChange={(e) => handleChange('lotId', e.target.value)}
              disabled={!form.skuId || loadingLots}
            >
              <option value="">{loadingLots ? 'Loading lots…' : 'Select lot'}</option>
              {lots.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.lot_number} — {l.quantity_on_hand} @ {l.location_code} (exp {new Date(l.expiration_date).toLocaleDateString()})
                </option>
              ))}
            </select>
            {form.skuId && !loadingLots && lots.length === 0 && (
              <span className={styles.sku}>No lots — receive stock first.</span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Adjustment Type</label>
            <select
              className={styles.input}
              value={form.adjustmentType}
              onChange={(e) => handleChange('adjustmentType', e.target.value)}
            >
              {adjustmentTypeOptions.map((opt) => (
                <option key={opt} value={opt === 'Select Type' ? '' : opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Quantity</label>
            <input
              type="number"
              min={1}
              className={styles.input}
              placeholder="Enter quantity"
              value={form.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Reason</label>
            <select
              className={styles.input}
              value={form.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
            >
              {reasonOptions.map((opt) => (
                <option key={opt} value={opt === 'Select Reason' ? '' : opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className={styles.notesField}>
            <label className={styles.label}>Notes</label>
            <textarea
              className={styles.textarea}
              placeholder="Add notes..."
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div style={{ margin: '8px 0', padding: 10, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div className={styles.buttons}>
          <button type="submit" className={styles.confirmButton} disabled={submitting}>
            {submitting ? 'Applying…' : 'Confirm Adjustment'}
          </button>
          <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
